import clsx from 'clsx';
import { useState } from 'react';

import { CollectionIcon } from '~components/icons/CollectionIcon';
import { ThumbnailIcon } from '~components/icons/ThumbnailIcon';
import { Asset } from '~modules/media-library/model/Asset';
import { AssetActions } from '~modules/media-library/model/AssetActions';
import { isAssetCollection } from '~modules/media-library/model/AssetFactory';
import { ViewVariant } from '~modules/media-library/model/ViewVariant';
import { AssetIdTag } from '~modules/media-library/view/AssetIdTag';
import { FavouriteButton } from '~modules/media-library/view/FavouriteButton';
import { Bubbles } from '~modules/media-library/view/MetaBubble/Bubbles';

type TeaserFigureProps = Resolve<{
  asset: Asset;
  AssetActions: AssetActions;
  variant?: ViewVariant;
  isHovering?: boolean;
}>;

export function TeaserFigure({
  asset,
  variant = ViewVariant.DEFAULT,
  AssetActions,
  isHovering = false,
}: TeaserFigureProps) {
  // Derived values
  const assetId = asset.id;
  const title = asset.attributes.title;
  const previewUrl =
    ViewVariant.TILE_LARGE !== variant && ViewVariant.TILE_MEDIUM !== variant && Boolean(asset.previews.list)
      ? asset.previews.list
      : asset.previews.details;
  const isFavourite = asset.infos.isFavourite || false;
  const isCollection = isAssetCollection(asset);

  const [previewError, setPreviewError] = useState(false);

  const onImageError = function () {
    setPreviewError(true);
  };

  return (
    <figure
      className={clsx(
        'ml--teaser-figure',
        'aspect-ratio-1/1 relative overflow-hidden transition bg-opacity-50 shadow-inner',
        !isCollection && 'bg-silver',
        variant === ViewVariant.TILE_LARGE && '',
        variant !== ViewVariant.TILE_LARGE && 'rounded-md',
        variant === ViewVariant.TILE_MEDIUM && 'rounded-b-none',
        isHovering && 'opacity-80',
        variant === ViewVariant.LIST && 'h-14 w-14 flex-shrink-0',
        'preview-' + asset.type.toLowerCase(),
      )}
    >
      {!!previewUrl && !previewError ? (
        <img
          className="absolute inset-0 w-full h-full object-cover"
          src={previewUrl}
          alt={title}
          onError={onImageError}
        />
      ) : (
        <div className="absolute inset-0 w-full h-full flex items-center justify-center">
          {isCollection ? (
            <CollectionIcon className="w-1/2 h-1/2 mt-1" />
          ) : (
            <ThumbnailIcon className="text-black opacity-30 w-1/2" />
          )}
        </div>
      )}

      {variant === ViewVariant.TILE_MEDIUM || variant === ViewVariant.TILE_LARGE ? (
        <AssetIdTag asset={asset} onClick={AssetActions.onCopyId} className="absolute bottom-0 right-0 m-2" />
      ) : null}

      {variant !== ViewVariant.LIST ? (
        <div
          className={clsx(
            'absolute top-0 left-0 right-0 p-2 bg-opacity-100 flex justify-between',
            variant === ViewVariant.TILE_SMALL && 'origin-top transform transition',
            variant === ViewVariant.TILE_SMALL && !isHovering && '-translate-y-2 opacity-0',
            variant === ViewVariant.TILE_SMALL && isHovering && 'translate-y-0 opacity-100',
          )}
        >
          <FavouriteButton
            assetId={assetId}
            onToggle={AssetActions.onToggleFavourite}
            isFavourite={isFavourite}
            small
          />
          <Bubbles asset={asset} />
        </div>
      ) : null}
    </figure>
  );
}
